import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Plus, Edit2, Trash2, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('blog');
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showPostEditor, setShowPostEditor] = useState(false);
  const [showLeadEditor, setShowLeadEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: user?.name || '',
    image_url: '',
    published: false
  });
  const [leadFormData, setLeadFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    province: '',
    enquiry_type: 'General Enquiry',
    message: ''
  });

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?published_only=false`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      alert('Unable to load blog posts. Please try again.');
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      alert('Unable to load leads. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchPosts();
    fetchLeads();
  }, [user, navigate, fetchPosts, fetchLeads]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: user?.name || '',
      image_url: '',
      published: false
    });
    setShowPostEditor(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      image_url: post.image_url || '',
      published: post.published
    });
    setShowPostEditor(true);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/${postId}`);
      fetchPosts();
    } catch (error) {
      alert('Failed to delete post');
    }
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData);
      } else {
        await axios.post(`${API}/blog`, formData);
      }
      setShowPostEditor(false);
      fetchPosts();
    } catch (error) {
      alert('Failed to save post');
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  // Lead Management Functions
  const handleNewLead = () => {
    setEditingLead(null);
    setLeadFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      province: '',
      enquiry_type: 'General Enquiry',
      message: ''
    });
    setShowLeadEditor(true);
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setLeadFormData({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      company: lead.company || '',
      province: lead.province || '',
      enquiry_type: lead.enquiry_type,
      message: lead.message
    });
    setShowLeadEditor(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      fetchLeads();
    } catch (error) {
      alert('Failed to delete lead');
    }
  };

  const handleSaveLead = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, leadFormData);
      } else {
        await axios.post(`${API}/leads`, leadFormData);
      }
      setShowLeadEditor(false);
      fetchLeads();
    } catch (error) {
      alert('Failed to save lead');
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  if (showPostEditor) {
    return (
      <div className="min-h-screen bg-[#F4F7F5] py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white p-8 rounded-2xl border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-[#0A3D2E] font-['Outfit']">
                {editingPost ? 'Edit Post' : 'New Post'}
              </h2>
              <button
                onClick={() => setShowPostEditor(false)}
                className="px-6 py-2 text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSavePost} data-testid="post-editor-form">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Title</label>
                  <input
                    type="text"
                    data-testid="post-title-input"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Slug</label>
                  <input
                    type="text"
                    data-testid="post-slug-input"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Excerpt</label>
                  <textarea
                    data-testid="post-excerpt-input"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    required
                    rows={3}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Content</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Image URL</label>
                  <input
                    type="url"
                    data-testid="post-image-url-input"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Author</label>
                  <input
                    type="text"
                    data-testid="post-author-input"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    data-testid="post-published-checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-5 h-5 text-[#0A3D2E] focus:ring-[#CDFF00] border-[#E2E8F0] rounded"
                  />
                  <label className="ml-3 text-sm font-medium text-[#0A3D2E]">Published</label>
                </div>

                <button
                  type="submit"
                  data-testid="post-save-button"
                  className="w-full px-6 py-3 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none"
                >
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (showLeadEditor) {
    const provinces = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'];
    const enquiryTypes = ['General Enquiry', 'Residential Installation', 'Commercial Installation', 'Fleet Solutions', 'Portable Chargers', 'Maintenance & Support', 'Partnership Opportunity'];

    return (
      <div className="min-h-screen bg-[#F4F7F5] py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white p-8 rounded-2xl border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-[#0A3D2E] font-['Outfit']">
                {editingLead ? 'Edit Lead' : 'New Lead'}
              </h2>
              <button
                onClick={() => setShowLeadEditor(false)}
                className="px-6 py-2 text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSaveLead}>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Name *</label>
                    <input
                      type="text"
                      value={leadFormData.name}
                      onChange={(e) => setLeadFormData({ ...leadFormData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Email *</label>
                    <input
                      type="email"
                      value={leadFormData.email}
                      onChange={(e) => setLeadFormData({ ...leadFormData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={leadFormData.phone}
                      onChange={(e) => setLeadFormData({ ...leadFormData, phone: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Company</label>
                    <input
                      type="text"
                      value={leadFormData.company}
                      onChange={(e) => setLeadFormData({ ...leadFormData, company: e.target.value })}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Province</label>
                    <select
                      value={leadFormData.province}
                      onChange={(e) => setLeadFormData({ ...leadFormData, province: e.target.value })}
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    >
                      <option value="">Select Province</option>
                      {provinces.map((prov) => (
                        <option key={prov} value={prov}>{prov}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Enquiry Type *</label>
                    <select
                      value={leadFormData.enquiry_type}
                      onChange={(e) => setLeadFormData({ ...leadFormData, enquiry_type: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none"
                    >
                      {enquiryTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A3D2E] mb-2">Message *</label>
                  <textarea
                    value={leadFormData.message}
                    onChange={(e) => setLeadFormData({ ...leadFormData, message: e.target.value })}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-[#E2E8F0] rounded-2xl focus:ring-2 focus:ring-[#CDFF00] focus:outline-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none"
                >
                  {editingLead ? 'Update Lead' : 'Create Lead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F5]" data-testid="admin-dashboard">
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#0A3D2E] font-['Outfit']">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center space-x-2 px-4 py-2 text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex space-x-4 mb-8 border-b border-[#E2E8F0]">
          <button
            onClick={() => setActiveTab('blog')}
            data-testid="tab-blog"
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'blog'
                ? 'text-[#0A3D2E] border-b-2 border-[#CDFF00]'
                : 'text-[#4A5D54] hover:text-[#0A3D2E]'
            }`}
          >
            Blog Posts
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            data-testid="tab-leads"
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'leads'
                ? 'text-[#0A3D2E] border-b-2 border-[#CDFF00]'
                : 'text-[#4A5D54] hover:text-[#0A3D2E]'
            }`}
          >
            Leads ({leads.length})
          </button>
        </div>

        {activeTab === 'blog' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-[#0A3D2E] font-['Outfit']">
                Blog Posts ({posts.length})
              </h2>
              <button
                onClick={handleNewPost}
                data-testid="new-post-button"
                className="flex items-center space-x-2 px-6 py-3 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors"
              >
                <Plus size={20} />
                <span>New Post</span>
              </button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
              {posts.length === 0 ? (
                <div className="p-8 text-center text-[#4A5D54]">
                  No blog posts yet. Create your first post!
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F4F7F5]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Title</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Author</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Status</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-[#0A3D2E]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {posts.map((post) => (
                        <tr key={post.id}>
                          <td className="px-6 py-4 text-[#0A3D2E] font-medium">{post.title}</td>
                          <td className="px-6 py-4 text-[#4A5D54]">{post.author}</td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                post.published
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {post.published ? 'Published' : 'Draft'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              {post.published && (
                                <a
                                  href={`/blog/${post.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
                                >
                                  <Eye size={18} />
                                </a>
                              )}
                              <button
                                onClick={() => handleEditPost(post)}
                                data-testid={`edit-post-${post.id}`}
                                className="p-2 text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => handleDeletePost(post.id)}
                                data-testid={`delete-post-${post.id}`}
                                className="p-2 text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'leads' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-[#0A3D2E] font-['Outfit']">
                Contact Leads ({leads.length})
              </h2>
              <button
                onClick={handleNewLead}
                data-testid="new-lead-button"
                className="flex items-center space-x-2 px-6 py-3 bg-[#0A3D2E] text-white rounded-full font-semibold hover:bg-[#07291F] transition-colors"
              >
                <Plus size={20} />
                <span>New Lead</span>
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
              {leads.length === 0 ? (
                <div className="p-8 text-center text-[#4A5D54]">No leads yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#F4F7F5]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Name</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Email</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Phone</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Company</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Province</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Enquiry Type</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Message</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-[#0A3D2E]">Date</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-[#0A3D2E]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {leads.map((lead) => (
                        <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                          <td className="px-6 py-4 text-[#0A3D2E] font-medium">{lead.name}</td>
                          <td className="px-6 py-4 text-[#4A5D54]">{lead.email}</td>
                          <td className="px-6 py-4 text-[#4A5D54]">{lead.phone}</td>
                          <td className="px-6 py-4 text-[#4A5D54]">{lead.company || '—'}</td>
                          <td className="px-6 py-4 text-[#4A5D54]">{lead.province || '—'}</td>
                          <td className="px-6 py-4 text-[#4A5D54]">{lead.enquiry_type}</td>
                          <td className="px-6 py-4 text-[#4A5D54] max-w-xs">
                            <div className="truncate" title={lead.message}>
                              {lead.message}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-[#4A5D54] whitespace-nowrap">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleEditLead(lead)}
                                data-testid={`edit-lead-${lead.id}`}
                                title="Edit lead"
                                className="p-2 text-[#4A5D54] hover:text-[#0A3D2E] transition-colors"
                              >
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteLead(lead.id)}
                                data-testid={`delete-lead-${lead.id}`}
                                title="Delete lead"
                                className="p-2 text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
